import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { Layout } from "@/components/site/Layout";
import { TitleManager } from "@/components/site/TitleManager";
import Home from "@/pages/Home"; // eager: landing paints instantly

// Route-level code splitting — each page ships in its own chunk so the
// initial (and PWA) load only downloads what the first screen needs.
const DomainPage = lazy(() => import("@/pages/DomainPage"));
const NiceHome = lazy(() => import("@/pages/NiceHome"));
const AiMedicine = lazy(() => import("@/pages/AiMedicine"));
const Athena = lazy(() => import("@/pages/Athena"));
const Contact = lazy(() => import("@/pages/Contact"));
const Publications = lazy(() => import("@/pages/Publications"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminPublications = lazy(() => import("@/pages/AdminPublications"));
const AdminInvestigators = lazy(() => import("@/pages/AdminInvestigators"));
const AdminReminders = lazy(() => import("@/pages/AdminReminders"));
const AdminPages = lazy(() => import("@/pages/AdminPages"));
const PublicPage = lazy(() => import("@/pages/PublicPage"));
const Founder = lazy(() => import("@/pages/Founder"));
const Investigators = lazy(() => import("@/pages/Investigators"));
const InvestigatorProfile = lazy(() => import("@/pages/InvestigatorProfile"));
const Login = lazy(() => import("@/pages/Login"));
const Member = lazy(() => import("@/pages/Member"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Membership = lazy(() => import("@/pages/Membership"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const AthenaApp = lazy(() => import("@/pages/AthenaApp"));
const AthenaLauncher = lazy(() => import("@/pages/AthenaLauncher"));
const AdvisorChat = lazy(() => import("@/pages/AdvisorChat"));
const WebApps = lazy(() => import("@/pages/WebApps"));
const WebAppDetail = lazy(() => import("@/pages/WebAppDetail"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));

function PageLoader() {
  return (
    <div data-testid="route-loader" className="min-h-screen flex items-center justify-center bg-vv-bg">
      <div className="w-9 h-9 rounded-full border-2 border-vv-accent/25 border-t-vv-accent animate-spin" />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
      <BrowserRouter>
        <TitleManager />
        <Layout>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<DomainPage slug="about" />} />
            <Route path="/healthcare" element={<DomainPage slug="healthcare" />} />
            <Route path="/research" element={<DomainPage slug="research" />} />
            <Route path="/innovation" element={<DomainPage slug="innovation" />} />
            <Route path="/publications" element={<Publications />} />
            <Route path="/founder" element={<Founder />} />
            <Route path="/investigators" element={<Investigators />} />
            <Route path="/investigators/:id" element={<InvestigatorProfile />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/member" element={<Member />} />
            <Route path="/member/athena" element={<AthenaApp />} />
            <Route path="/app" element={<AthenaLauncher />} />
            <Route path="/member/advisor" element={<AdvisorChat />} />
            <Route path="/membership" element={<Membership />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/collaborations" element={<DomainPage slug="collaborations" />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/privacy" element={<LegalPage slug="privacy" />} />
            <Route path="/terms" element={<LegalPage slug="terms" />} />
            <Route path="/refund-policy" element={<LegalPage slug="refund-policy" />} />

            <Route path="/nice" element={<NiceHome />} />
            <Route path="/nice/about" element={<DomainPage slug="nice-about" theme="gold" />} />
            <Route path="/nice/cardiometabolic" element={<DomainPage slug="nice-cardiometabolic" theme="gold" />} />
            <Route path="/nice/precision-health" element={<DomainPage slug="nice-precision-health" theme="gold" />} />
            <Route path="/nice/ai" element={<AiMedicine />} />
            <Route path="/nice/athena" element={<Athena />} />
            <Route path="/nice/environmental" element={<DomainPage slug="nice-environmental" theme="gold" />} />
            <Route path="/nice/aging" element={<DomainPage slug="nice-aging" theme="gold" />} />
            <Route path="/nice/research-programs" element={<DomainPage slug="nice-research-programs" theme="gold" />} />
            <Route path="/nice/clinical-research" element={<DomainPage slug="nice-clinical-research" theme="gold" />} />
            <Route path="/nice/education" element={<DomainPage slug="nice-education" theme="gold" />} />
            <Route path="/nice/network" element={<DomainPage slug="nice-network" theme="gold" />} />
            <Route path="/nice/webapps" element={<WebApps />} />
            <Route path="/nice/webapps/:slug" element={<WebAppDetail />} />

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/publications" element={<AdminPublications />} />
            <Route path="/admin/investigators" element={<AdminInvestigators />} />
            <Route path="/admin/reminders" element={<AdminReminders />} />
            <Route path="/admin/pages" element={<AdminPages />} />
            <Route path="/p/:slug" element={<PublicPage />} />
          </Routes>
          </Suspense>
        </Layout>
        <Toaster theme="dark" position="bottom-right" />
      </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
